import type { ScenePhase } from 'js/src/data/html-design';

/**
 * How many phases a scene of this length wants, as the authoring guide's ladder: under 5s there is
 * room for one, a 10s scene wants two, a 15s scene three. A floor rather than a target — a scene may
 * always carry more.
 */
export function minimumPhaseCount(sceneSeconds: number): number {
    return Math.max(1, Math.floor(sceneSeconds / 5));
}

/**
 * The longest stretch of the scene that may pass with no phase beginning in it. Applies to the run
 * up to the first phase, every run between two phases, and the tail after the last one, so a single
 * bound catches front-loading, a mid-scene gap and a dead tail alike. The floor keeps a legitimate
 * single-phase short scene legal; past ~14s a share of the scene is the binding constraint.
 */
export function maximumUnscheduledGapSeconds(sceneSeconds: number): number {
    return Math.max(5, sceneSeconds * 0.35);
}

/** A phase time as it should read back to the agent — trailing zeroes are noise in a sentence. */
function seconds(value: number): string {
    return `${value.toFixed(1).replace(/\.0$/, '')}s`;
}

/** The unscheduled runs of the scene: start→first phase, phase→phase, last phase→end. */
function gaps(phases: readonly ScenePhase[], sceneSeconds: number): { from: number; to: number }[] {
    const boundaries = [0, ...phases.map((phase) => phase.at), sceneSeconds];
    const runs: { from: number; to: number }[] = [];
    for (let i = 0; i < boundaries.length - 1; i++) {
        runs.push({ from: boundaries[i], to: boundaries[i + 1] });
    }
    return runs;
}

/**
 * Why this phase schedule can't be approved, or null when it can. The checks run cheapest-and-most-
 * fundamental first so the agent is told the one thing to fix rather than a pile of consequences of
 * the same mistake.
 */
export function scheduleRejection(
    phases: readonly ScenePhase[],
    sceneSeconds: number,
): string | null {
    if (phases.length === 0) {
        return (
            'The plan schedules no phases. Every graphic has at least one phase — the moment it ' +
            'arrives — and a longer scene needs several, so give phases at least one entry.'
        );
    }

    for (let i = 1; i < phases.length; i++) {
        if (phases[i].at <= phases[i - 1].at) {
            return (
                `Phase ${i + 1} is scheduled at ${seconds(phases[i].at)}, which is not after phase ` +
                `${i} at ${seconds(phases[i - 1].at)}. List the phases in ascending order of \`at\`, ` +
                'in the order the viewer sees them.'
            );
        }
    }

    // `enters` is optional so a settle can be declared honestly rather than dressed up as an
    // arrival, but a phase still has to advance the graphic somehow. One that declares nothing is
    // the padding the schedule exists to keep out.
    const empty = phases.findIndex(
        (phase) => !phase.enters?.trim() && !phase.transforms?.trim() && !phase.exits?.trim(),
    );
    if (empty !== -1) {
        // Named for the phase's own position: telling a later phase it may declare `enters` sends
        // it back round to the rearrangement check below, having done what it was asked.
        const remedy =
            empty === 0
                ? 'Say what the phase does — a new element in `enters`, a rearrangement in ' +
                  '`transforms`, or what goes in `exits`'
                : 'Say what the phase does — what leaves in `exits`, or what moves aside in ' +
                  '`transforms`, since every phase after the first has to rearrange the ' +
                  'frame rather than only add to it';
        return (
            `Phase ${empty + 1} at ${seconds(phases[empty].at)} says nothing happens: it brings ` +
            `nothing in, changes nothing already on screen, and removes nothing. ${remedy} — or ` +
            'drop the phase and give its time to the ones either side.'
        );
    }

    const first = phases[0].at;
    if (first < 0 || first > 0.5) {
        return (
            `The first phase is scheduled at ${seconds(first)}, so the scene opens on a frame that ` +
            'holds until then. The first phase is the graphic arriving: schedule it at 0.'
        );
    }

    const last = phases[phases.length - 1].at;
    if (last >= sceneSeconds) {
        return (
            `Phase ${phases.length} is scheduled at ${seconds(last)} but the scene ends at ` +
            `${seconds(sceneSeconds)}, so it never reaches the viewer. Every phase has to begin ` +
            'inside the scene, with room left to play out.'
        );
    }

    const required = minimumPhaseCount(sceneSeconds);
    if (phases.length < required) {
        return (
            `A ${seconds(sceneSeconds)} scene wants at least ${required} phases and the plan has ` +
            `${phases.length}. The extra time is not room for a second idea — it is room to develop ` +
            'the one you have: a setup, then the thing that lands it, then what it means. Break the ' +
            'graphic into phases that each move it on: the first arrives, and every one after it ' +
            'says what that arrival costs the frame in `transforms` or `exits`.'
        );
    }

    const widest = gaps(phases, sceneSeconds).reduce((a, b) =>
        b.to - b.from > a.to - a.from ? b : a,
    );
    const allowed = maximumUnscheduledGapSeconds(sceneSeconds);
    if (widest.to - widest.from > allowed) {
        return (
            `From ${seconds(widest.from)} to ${seconds(widest.to)} the plan has nothing scheduled — ` +
            `${seconds(widest.to - widest.from)} of a ${seconds(sceneSeconds)} scene with no phase ` +
            'beginning in it. That stretch is where a graphic dies, or where filler motion gets ' +
            'invented to cover it. Re-pace the phases so none of the scene waits longer than ' +
            `${seconds(allowed)} for the next one, and so the last phase lands near the end.`
        );
    }

    // Last, so a schedule with a structural fault is told about that rather than about a phase
    // whose shape is a consequence of it.
    //
    // The opening phase is the graphic arriving, so adding is all it can do. Every phase after it
    // has a frame to work with, and one that only adds leaves what is already there untouched —
    // the graphic silts up and then freezes. Requiring the rearrangement is what makes two phases
    // read as one thought rather than as a slideshow hiding inside a scene.
    const adding = phases.findIndex(
        (phase, index) => index > 0 && !phase.transforms?.trim() && !phase.exits?.trim(),
    );
    if (adding !== -1) {
        return (
            `Phase ${adding + 1} at ${seconds(phases[adding].at)} only brings something new: ` +
            'nothing already on screen changes to make room for it, and nothing goes. That is a ' +
            'graphic adding to itself until it is full. Say what the arrival costs the frame — ' +
            'what leaves in `exits`, or what moves aside in `transforms`. Only the opening ' +
            'phase is allowed to simply arrive.'
        );
    }

    return null;
}
